<script setup lang="ts" generic="T = any">
import { useFieldTexts, useValidationRules } from '@/composables';
import { useValidationErrorStore } from '@/stores/validationErrorStore';
import {
    useAttrs, computed, ref, onMounted,
} from 'vue';
import type { VAutocomplete } from 'vuetify/components';
import { generateInputId } from '@/utils/idUtils';

type PropsType = {
    modelValue?: T | T[] | null | undefined;
    items?: any[];
    label?: string;
    placeholder?: string;
    variant?: VAutocomplete['$props']['variant'];
    density?: VAutocomplete['$props']['density'];
    color?: string;
    clearable?: boolean;
    loading?: boolean;
    isLabelTranslated?: boolean;
    isPlaceholderTranslated?: boolean;
    showRequiredVisual?: boolean;
    itemTitle?: string;
    itemValue?: string;
    multiple?: boolean;
    chips?: boolean;
    closableChips?: boolean;
    noFilter?: boolean;
    returnObject?: boolean;
    fieldName?: string;
    panelIndex?: number;
    tabId?: string;
    name?: string;
    dataType?: string;
};

const props = withDefaults(defineProps<PropsType>(), {
    variant: 'outlined',
    density: 'compact',
    clearable: true,
    loading: false,
    isLabelTranslated: true,
    isPlaceholderTranslated: false,
    showRequiredVisual: true,
    itemTitle: 'title',
    itemValue: 'value',
    multiple: false,
    chips: false,
    closableChips: false,
    noFilter: false,
    returnObject: false,
    dataType: 'autocomplete',
});

const emit = defineEmits<{
    'update:modelValue': [value: T | T[] | null | undefined];
    'update:search': [value: string];
    'click:clear': [];
    focus: [event: FocusEvent];
    blur: [event: FocusEvent];
    'update:menu': [value: boolean];
}>();

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();

const { getLabel, getPlaceholder } = useFieldTexts({
    label: props.label,
    placeholder: props.placeholder,
    isTranslated: props.isLabelTranslated,
});
const { rules, isRequired } = useValidationRules(attrs);

const validationStore = useValidationErrorStore();
const resolvedFieldName = props.fieldName || props.label || 'field';
const autocompleteRef = ref<VAutocomplete | null>(null);

const inputValidation = validationStore.currentFormId
    ? validationStore.registerInput(resolvedFieldName, props.panelIndex ?? null, props.tabId ?? null)
    : null;

const computedId = computed(() => {
    return inputValidation?.inputId || generateInputId('autocomplete');
});

onMounted(() => {
    const input = autocompleteRef.value?.$el?.querySelector('input');
    if (input) {
        input.dataset.type = props.dataType;
    }
});
</script>

<template>
    <VAutocomplete
        ref="autocompleteRef"
        :id="computedId"
        :model-value="modelValue"
        :items="items"
        :label="getLabel"
        :placeholder="getPlaceholder"
        :variant="variant"
        :density="density"
        :color="color"
        :clearable="clearable"
        :loading="loading"
        :item-title="itemTitle"
        :item-value="itemValue"
        :multiple="multiple"
        :chips="chips"
        :closable-chips="closableChips"
        :no-filter="noFilter"
        :return-object="returnObject"
        :name="name"
        :class="[$attrs.class, { required: isRequired && showRequiredVisual }]"
        v-bind="{
            ...$attrs,
            rules: rules,
        }"
        @update:model-value="emit('update:modelValue', $event)"
        @update:search="emit('update:search', $event)"
        @click:clear="emit('click:clear')"
        @focus="emit('focus', $event)"
        @blur="emit('blur', $event)"
        @update:menu="emit('update:menu', $event)"
    >
        <template
            v-if="$slots.prepend"
            #prepend
        >
            <slot name="prepend" />
        </template>

        <template
            v-if="$slots.append"
            #append
        >
            <slot name="append" />
        </template>

        <template
            v-if="$slots['prepend-item']"
            #prepend-item
        >
            <slot name="prepend-item" />
        </template>

        <template
            v-if="$slots.item"
            #item="{ props: itemProps, item }"
        >
            <slot
                name="item"
                :props="itemProps"
                :item="item"
            />
        </template>

        <template
            v-if="$slots.chip"
            #chip="{ props: chipProps, item, index }"
        >
            <slot
                name="chip"
                :props="chipProps"
                :item="item"
                :index="index"
            />
        </template>

        <template
            v-if="$slots['no-data']"
            #no-data
        >
            <slot name="no-data" />
        </template>
    </VAutocomplete>
</template>
